import { createSelector } from '@reduxjs/toolkit';

import {
  Asset,
  AssetContent_Generated_CBCT_Ortho_PDF,
  AssetType,
  GeneratedReportAsset,
} from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { MedicalImageInterface } from '@/shared/config';
import { getCBCTMetaData, getFileSrc } from '@/shared/lib';

import { RootState } from '@/app/model/store';

import {
  checkHasScale,
  getAssetMediaKind,
  getGeneratedReportOrder,
  getMedicalImageGroupName,
  getMedicalImageInfo,
  getMedicalImageKind,
  makeAnnotations,
  makeImageViewOptions,
} from '../lib';

import { assetsAdapter } from './assetsSlice';

const assetsSelectors = assetsAdapter.getSelectors(
  (state: RootState) => state.assets,
);

export const { selectAll, selectById, selectEntities } = assetsSelectors;

export const selectMedicalImages = createSelector(
  [assetsSelectors.selectAll],
  (assets) =>
    assets.reduce((accumulator, asset) => {
      if (asset.Kind.case === 'GeneratedReport') {
        return [...accumulator, asset.Kind.value];
      }

      return accumulator;
    }, [] as GeneratedReportAsset[]),
);

export const selectCBCTPanoReformatSplitAsset = (reportID: string) =>
  createSelector([assetsSelectors.selectAll], (assets) => {
    const splitAsset = assets.find((asset) => {
      if (asset.Kind.case === 'GeneratedReport') {
        const generatedReportAsset = asset.Kind.value;

        if (
          generatedReportAsset.Belonging?.ReportID === reportID &&
          generatedReportAsset.Content.case === 'CBCTGPPanoramaReformatSplit'
        ) {
          return generatedReportAsset.Content.value;
        }
      }
    });

    return splitAsset;
  });

export const selectMedicalImagesByToothID = (toothID: string) =>
  createSelector(selectMedicalImages, (medicalImages) =>
    medicalImages.filter(
      (medicalImage) => medicalImage?.Belonging?.ToothID === toothID,
    ),
  );

export const selectByToothID = (toothID: string) =>
  createSelector(assetsSelectors.selectAll, (medicalImages) =>
    medicalImages.filter((medicalImage) => {
      const generatedReportAsset =
        medicalImage.Kind.case === 'GeneratedReport'
          ? medicalImage.Kind.value
          : undefined;

      const reportAsset =
        medicalImage.Kind.case === 'Report'
          ? medicalImage.Kind.value
          : undefined;

      return (
        generatedReportAsset?.Belonging?.ToothID === toothID ||
        reportAsset?.Belonging?.ToothID === toothID
      );
    }),
  );

export const selectByReportID = (reportID: string) =>
  createSelector(assetsSelectors.selectAll, (medicalImages) =>
    medicalImages.filter(
      (medicalImage) =>
        medicalImage.Kind.case === 'GeneratedReport' &&
        medicalImage.Kind.value.Belonging?.ReportID === reportID,
    ),
  );

export const selectCBCTOrthoPDFUrl = (reportID: string) =>
  createSelector(selectByReportID(reportID), (assets) => {
    const targetAsset = assets.find(
      (asset) =>
        asset.Kind.case === 'GeneratedReport' &&
        asset.Kind.value.Belonging?.ReportID === reportID &&
        asset.Kind.value.Content.case === 'CBCTOrthoPDF',
    );

    const pdfUrl = getFileSrc(
      (
        targetAsset?.Kind.value?.Content
          .value as AssetContent_Generated_CBCT_Ortho_PDF
      )?.PDF?.ID ?? '',
    );

    return pdfUrl;
  });

export const selectCBCTGPPanoImageByReportID = (reportID: string) =>
  createSelector(selectMedicalImages, (medicalImages) =>
    medicalImages.find(
      (medicalImage) =>
        medicalImage?.Belonging?.ReportID === reportID &&
        medicalImage.Content.case === 'CBCTGPPanoramaReformatGeneral',
    ),
  );

export const selectEndodonticPanoImageByReportID = (reportID: string) =>
  createSelector(selectMedicalImages, (medicalImages) =>
    medicalImages.find(
      (medicalImage) =>
        medicalImage?.Belonging?.ReportID === reportID &&
        medicalImage.Content.case === 'CBCTEndoPanoramaSplit',
    ),
  );

export const selectCBCTGPPanoImageSplit_DEPRICATED = (reportID: string) =>
  createSelector(selectMedicalImages, (medicalImages) => {
    const split = medicalImages.find(
      (medicalImage) =>
        medicalImage?.Belonging?.ReportID === reportID &&
        medicalImage?.Content.case === 'CBCTGPPanoramaReformatSplit',
    );

    return split;
  });

export const selectGPAssetByReportID = (reportID: string) =>
  createSelector([assetsSelectors.selectAll], (assets) =>
    assets.find(
      (asset) =>
        asset.Kind.case === 'GeneratedReport' &&
        asset.Kind.value.Belonging?.ReportID === reportID &&
        asset.Kind.value.Content.case === 'CBCTGPPanoramaReformatGeneral',
    ),
  );

export const selectEndodonticAssetByReportID = (reportID: string) =>
  createSelector([assetsSelectors.selectAll], (assets) =>
    assets.find(
      (asset) =>
        asset.Kind.case === 'GeneratedReport' &&
        asset.Kind.value.Belonging?.ReportID === reportID &&
        asset.Kind.value.Content.case === 'CBCTEndoPanoramaSplit',
    ),
  );

export const selectCBCTMedicalImagesByToothID = (toothID: string) =>
  createSelector(selectMedicalImagesByToothID(toothID), (medicalImages) =>
    medicalImages
      .filter(
        (medicalImage) => medicalImage.Content.case === 'CBCTGPToothSlice',
      )
      .map((medicalImage) => medicalImage.Content.value),
  );

export const selectCBCTToothPathologyMedicalImage = (toothID: string) =>
  createSelector(selectMedicalImagesByToothID(toothID), (medicalImages) =>
    medicalImages
      .filter(
        (medicalImage) =>
          medicalImage.Content.case === 'CBCTToothPathologySlice',
      )
      .map((medicalImage) => medicalImage.Content.value),
  );

export const selectIOXRayImagesByStudyID = (studyID: string) =>
  createSelector(assetsSelectors.selectAll, (assets) =>
    assets.filter(
      (asset) =>
        asset.Kind.case === 'Study' && asset.Kind.value.StudyID === studyID,
    ),
  );

// TODO: There are 1500+ assets in CBCT report, so find is not the best way.
// Study has asset id, so is this the same asset that we find here by study id?
export const selectCBCTMetaDataByStudyID = (studyID: string) =>
  createSelector([assetsSelectors.selectAll], (assets) =>
    getCBCTMetaData(studyID)(assets),
  );

export const selectMPRByToothID = (toothID: string) =>
  createSelector([assetsSelectors.selectAll], (assets) => {
    const targetAsset = assets.find(
      (asset) =>
        asset.Kind.case === 'GeneratedReport' &&
        asset.Kind.value.Belonging?.ToothID === toothID &&
        asset.Kind.value.Content.case === 'CBCTGPMPRSubVolume',
    );

    if (
      targetAsset &&
      targetAsset.Kind.case === 'GeneratedReport' &&
      targetAsset.Kind.value.Content.case === 'CBCTGPMPRSubVolume'
    ) {
      return targetAsset.Kind.value.Content.value;
    }

    return undefined;
  });

export const selectAssetByStudyID = (studyID: string) =>
  createSelector([assetsSelectors.selectAll], (assets) =>
    assets.find(
      (asset) =>
        asset.Kind.case === 'Study' && asset.Kind.value.StudyID === studyID,
    ),
  );

export const selectAssetByID = (assetID: string) => (state: RootState) =>
  selectById(state, assetID);

export const selectIsVideoAsset = (assetID: string) =>
  createSelector(selectAssetByID(assetID), (asset) => {
    if (asset?.Kind.case !== 'Study') {
      return false;
    }

    return asset.Kind.value.Content.case === 'DentalVideo';
  });

// Returns null when the asset is not yet loaded in the store,
// 'photo' or 'video' when it is. Use this instead of !isVideoAsset
// to avoid triggering image requests for video assets during loading.
export const selectAssetMediaKind = (assetID: string) =>
  createSelector(selectAssetByID(assetID), getAssetMediaKind);

export const selectAssetVideoFileID = (assetID: string) =>
  createSelector(selectAssetByID(assetID), (asset) => {
    if (asset?.Kind.case !== 'Study') {
      return undefined;
    }

    const content = asset.Kind.value.Content;

    if (content.case !== 'DentalVideo') {
      return undefined;
    }

    return content.value.Video?.ID;
  });

export const selectAssetsByIDs = (assetIDs: string[]) =>
  createSelector(assetsSelectors.selectEntities, (assetsEntities) =>
    assetIDs.reduce((assets, assetID) => {
      if (assetsEntities[assetID]) {
        return assets.concat(assetsEntities[assetID] as Asset);
      }

      return assets;
    }, [] as Asset[]),
  );

export const selectLoading = (state: RootState) => state.assets.loading;

export const selectPatientAssetsSnapshot = (state: RootState) =>
  state.assets.patientAssetsSnapshot;

export const selectReportAssetsSnapshot = (state: RootState) =>
  state.assets.reportAssetsSnapshot;

export const selectPatientAssetsSnapshotCompletedGeneration = (
  state: RootState,
) => state.assets.patientAssetsSnapshotCompletedGeneration;

export const selectReportAssetsSnapshotPatientCompletionAtRefreshStart = (
  state: RootState,
) => state.assets.reportAssetsSnapshotPatientCompletionAtRefreshStart;

export const makeSelectAssetsByIDs = (assetIDs: string[]) =>
  createSelector(assetsSelectors.selectEntities, (assets) =>
    assetIDs.map((assetID) => assets[assetID]),
  );

export const makeSelectorUserUploadedAssetsByToothID = (toothID: string) =>
  createSelector(assetsSelectors.selectAll, (assets) =>
    assets
      .filter(
        (asset) =>
          !asset.Deleted?.Deleted &&
          (asset.Type === AssetType.AssetType_Report_Uploaded_ToothSlice ||
            asset.Type === AssetType.AssetType_Report_Uploaded_MPRPlane) &&
          asset.Kind.case === 'Report' &&
          asset.Kind.value.Belonging?.ToothID === toothID,
      )
      .sort((a, b) => {
        const dateA = a?.Created?.At?.toDate().getUTCDate() ?? 0;
        const dateB = b?.Created?.At?.toDate().getUTCDate() ?? 0;

        return dateA - dateB;
      }),
  );

export const selectIsAssetsExistsByToothID = (toothID: string) =>
  createSelector(selectByToothID(toothID), (assets) => assets.length > 0);

export const makeMedicalImageSelector = (assetID: string) =>
  createSelector(
    selectAssetByID(assetID),
    (asset): MedicalImageInterface | undefined => {
      if (!asset) {
        return undefined;
      }

      const reportAsset =
        asset?.Kind.case === 'Report' ? asset.Kind.value : undefined;
      const { medicalImageID, medicalImagePath } = getMedicalImageInfo(asset);

      if (!medicalImageID) {
        return undefined;
      }

      const generatedReportAsset =
        asset?.Kind.case === 'GeneratedReport' ? asset.Kind.value : undefined;

      const annotations = makeAnnotations(
        generatedReportAsset?.MedicalImageFeatures?.Annotations?.Layers ??
          reportAsset?.MedicalImageFeatures?.Annotations?.Layers,
      );

      const viewOptions = makeImageViewOptions(
        generatedReportAsset?.MedicalImageFeatures?.ViewOptionsV2 ??
          reportAsset?.MedicalImageFeatures?.ViewOptionsV2,
      );

      const slice =
        generatedReportAsset?.Content.case === 'CBCTGPToothSlice'
          ? generatedReportAsset.Content.value
          : undefined;

      const pathologySlice =
        generatedReportAsset?.Content.case === 'CBCTToothPathologySlice'
          ? generatedReportAsset.Content.value
          : undefined;

      const regularMedicalImage =
        slice?.Metadata.case === 'Regular' ? slice?.Metadata.value : undefined;

      return {
        id: slice?.Slice?.ID || '',
        src: getFileSrc(medicalImageID),
        kind: getMedicalImageKind(reportAsset?.Content),
        image:
          slice && slice.Slice?.Metadata.case === 'Image'
            ? {
                Height: Number(slice.Slice.Metadata.value.Height),
                Width: Number(slice.Slice.Metadata.value.Width),
              }
            : undefined,
        path: medicalImagePath,
        assetID: asset.ID,
        groupName: getMedicalImageGroupName(asset),
        thicknessMm: regularMedicalImage?.Thickness,
        strideMm: regularMedicalImage?.Stride,
        annotations,
        viewOptions,
        order: getGeneratedReportOrder(generatedReportAsset),
        hasScale:
          checkHasScale(slice?.Scale) || checkHasScale(pathologySlice?.Scale),
      };
    },
  );
